import * as L from 'leaflet';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import './GeomanOverrides.css';
import { useLeafletContext } from '@react-leaflet/core';
import { PM } from 'leaflet';
import { useCallback, useLayoutEffect, useMemo } from 'react';
import { Position } from 'geojson';
import { useTranslation } from 'react-i18next';
import i18n from '@casops/i18n';
import { activePathOptions } from '../../layers/geofence-editor/GeofenceEditor';
import { GEOFENCE_EDITOR_LAYER_CLASS, SHAPE_EDITOR_PANE_NAME } from '../../mapConfig';

const isEditorLayer = (layer: L.Layer): boolean => {
  return (
    (layer instanceof L.Circle || layer instanceof L.Polygon) &&
    layer.options.className === GEOFENCE_EDITOR_LAYER_CLASS
  );
};

export interface CasGeomanControlsProps {
  createMode: boolean;
  onCircleRadiusChange: (radius: number) => void;
  onEditChanged?: (editing: boolean) => void; // Todo: Keeping track of the map edit mode will be a future enhancement.
  onShapeUpdated: (points: L.LatLng[], radius: number | null) => void;
}

export default function CasGeomanControls({
  createMode,
  onCircleRadiusChange,
  onEditChanged,
  onShapeUpdated,
}: CasGeomanControlsProps) {
  const { t } = useTranslation();

  // Customise the default Geoman language translations (such as modifying the button titles).
  // The full Geoman translation mapping can be found here: https://github.com/geoman-io/leaflet-geoman/blob/master/src/assets/translations/en.json
  const customTranslation = useMemo<PM.Translations>(
    () => ({
      buttonTitles: {
        drawPolyButton: t('geoman.buttonTitles.drawPolyButton'),
      },
    }),
    [t],
  );

  const { map } = useLeafletContext();

  const toolbarOptions: PM.ToolbarOptions = useMemo(() => {
    return {
      position: 'topleft',
      drawControls: createMode,
      drawPolygon: true,
      drawMarker: false,
      drawCircleMarker: false,
      drawPolyline: false,
      drawRectangle: false,
      drawText: false,
      editControls: true,
      dragMode: true,
      cutPolygon: false,
      removalMode: false,
      rotateMode: false,
    };
  }, [createMode]);

  const processLayerUpdate = useCallback(
    (layer: L.Layer) => {
      if (layer instanceof L.Circle) {
        const circle = layer as L.Circle;
        const points: L.LatLng[] = [circle.getLatLng().wrap()];
        onShapeUpdated(points, circle.getRadius());
      } else if (layer instanceof L.Polygon) {
        const polygon = layer as L.Polygon;
        // polygonLayer.getLatLngs() doesn't return a closed polygon but toGeoJSON does.
        const geojson2 = polygon.toGeoJSON(14);
        const positionData = geojson2.geometry.coordinates as Position[][];
        onShapeUpdated(
          positionData[0].map(a => L.latLng(a[1], a[0]).wrap()),
          null,
        );
      }
    },
    [onShapeUpdated],
  );

  const onLayerUpdate = useCallback<PM.UpdateEventHandler>(
    event => {
      if (event.shape === 'Circle' || event.shape === 'Polygon') {
        processLayerUpdate(event.layer);
      }
    },
    [processLayerUpdate],
  );

  const onCircleEditDrag = useCallback<L.LeafletEventHandlerFn>(
    event => {
      const circleLayer = event.target as L.Circle;
      if (circleLayer) {
        const circleRadius = Math.round(circleLayer.getRadius());
        onCircleRadiusChange(circleRadius);
      }
    },
    [onCircleRadiusChange],
  );

  const attachLayerChangeUpdates = useCallback(
    (layer: L.Layer) => {
      if (layer instanceof L.Circle) {
        layer.on('pm:update', onLayerUpdate);
        layer.on('pm:markerdrag', onCircleEditDrag);
      } else if (layer instanceof L.Polygon) {
        layer.on('pm:update', onLayerUpdate);
      }
    },
    [onCircleEditDrag, onLayerUpdate],
  );

  const detachLayerChangeUpdates = useCallback(
    (layer: L.Layer) => {
      if (layer instanceof L.Circle) {
        layer.off('pm:update', onLayerUpdate);
        layer.off('pm:markerdrag', onCircleEditDrag);
      } else if (layer instanceof L.Polygon) {
        layer.off('pm:update', onLayerUpdate);
      }
    },
    [onCircleEditDrag, onLayerUpdate],
  );

  // This callback is fired when ANY layer is added to the map - so filtering for only the edit layer is essential.
  const onLayerAdd = useCallback<L.LayerEventHandlerFn>(
    ({ layer }) => {
      if (isEditorLayer(layer)) {
        attachLayerChangeUpdates(layer);
      }
    },
    [attachLayerChangeUpdates],
  );

  // Fires when any layer is removed from the map. Must identify the same layer(s) as the onLayerAdd callback.
  const onLayerRemove = useCallback<L.LayerEventHandlerFn>(
    ({ layer }) => {
      if (isEditorLayer(layer)) {
        detachLayerChangeUpdates(layer);
      }
    },
    [detachLayerChangeUpdates],
  );

  const onShapeCreated = useCallback(
    // Leaflet is unaware of the geoman event shape, and vice versa.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: any) => {
      // Ensure only a single shape can be created.
      map.pm.disableDraw();
      map.pm.Toolbar.setButtonDisabled('Polygon', true);
      map.pm.Toolbar.setButtonDisabled('Circle', true);

      if (e.shape === 'Circle' || e.shape === 'Polygon') {
        processLayerUpdate(e.layer);
      }
    },
    // Exclude the map as a dependency to keep the function reference stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [processLayerUpdate],
  );

  // Circle radius tracking
  const onDrawStart = useCallback<PM.DrawStartEventHandler>(
    event => {
      const workingLayer = event.workingLayer;
      if (workingLayer && event.shape === 'Circle') {
        workingLayer.on('pm:change', onCircleEditDrag);
      }
    },
    [onCircleEditDrag],
  );

  // Active edit/draw mode tracking
  const onEditToggled = useCallback(
    (event: { enabled: boolean }) => {
      if (onEditChanged) {
        onEditChanged(event.enabled);
      }
    },
    [onEditChanged],
  );

  // This layout effect hook is used to setup and reset the geoman controls, layers, and event listeners.
  // It should not be allowed to re-execute when any of the data dependencies used in this hook change.
  // All functions that are used inside this hook should have stable references.
  useLayoutEffect(() => {
    if (!map.pm.controlsVisible()) {
      map.pm.addControls(toolbarOptions);

      map.pm.setGlobalOptions({
        templineStyle: { ...activePathOptions, radius: 0 }, // Style the placed lines.
        hintlineStyle: { ...activePathOptions, dashArray: [6, 6] }, // Style the active line.
        // Override the default layer pane for drawn layers to be slightly above the overlay pane.
        panes: { layerPane: SHAPE_EDITOR_PANE_NAME },
      });

      // Style the completed shape.
      map.pm.setPathOptions({
        ...activePathOptions,
      });

      // Override the default Geoman UI translations with custom translations.
      map.pm.setLang(
        'cas_ops' as PM.SupportLocales, // Any custom language name.
        customTranslation, // Partial map of Geoman translations.
        i18n.language, // Use the i18n detected language as the fallback language code for any Geoman translations that were not overridden.
      );

      // Note: Toolbar modifications get wiped unless they happen after the language is changed.
      map.pm.Toolbar.changeActionsOfControl('Polygon', ['cancel']);

      // Watch for geoman actions.
      map.on('pm:globaleditmodetoggled', onEditToggled);
      map.on('pm:globaldragmodetoggled', onEditToggled);
      map.on('pm:globaldrawmodetoggled', onEditToggled);
      map.on('pm:drawstart', onDrawStart);
      map.on('pm:create', onShapeCreated);

      // Watch for layer changes.
      map.on('layeradd', onLayerAdd);
      map.on('layerremove', onLayerRemove);
    }

    return () => {
      // Cancel any active drawing/editing. Otherwise the geoman active drawing layers and state can persist in the map outside of the current route.
      map.pm.disableDraw(); // Cancels any active drawing.
      map.pm.disableGlobalEditMode(); // Makes sure no layers are in edit mode.
      map.pm.disableGlobalDragMode(); // Note: Disabling drag mode does not appear have any additional effect, but it also doesn't hurt.

      // Remove the geoman UI controls. Note: this method only changes visibility but the DOM nodes persist.
      map.pm.removeControls();

      // Remove any geoman drawn layers from the map.
      const drawLayers = map.pm.getGeomanDrawLayers();
      for (let i = 0; i < drawLayers.length; i++) {
        map.removeLayer(drawLayers[i]);
      }

      // Remove Geoman listeners
      map.off('pm:globaleditmodetoggled', onEditToggled);
      map.off('pm:globaldragmodetoggled', onEditToggled);
      map.off('pm:globaldrawmodetoggled', onEditToggled);
      // @ts-expect-error: Leaflet is not aware of the geoman event shape.
      map.off('pm:drawStart', onDrawStart);
      map.off('pm:create', onShapeCreated);

      // Remove layer listeners. This must occur after all other cleanup so that layer removal events are still processed first.
      map.off('layeradd', onLayerAdd);
      map.off('layerremove', onLayerRemove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
